import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import {
  formatBudgetNumber,
  type SapBudgetIncreaseAccount,
} from '@/utils/approval/sapBudgetIncrease';
import { SapBudgetIncreaseRoot } from './styles';

type RowsProps = {
  department: string;
  yearMonth: string;
  account: SapBudgetIncreaseAccount | null;
  /** 증액 신청금액 칸 — 작성 화면은 입력칸, 저장 HTML 은 금액 글자 */
  amountCell: ReactNode;
  t: TFunction;
};

/** 레거시 #eas_budget_box 표의 colgroup·머리글·행(계정 코드 칸은 d-none) */
export const SapBudgetIncreaseRows = ({
  department,
  yearMonth,
  account,
  amountCell,
  t,
}: RowsProps) => (
  <>
    <colgroup>
      <col width="10%" />
    </colgroup>
    <thead>
      <tr>
        <th className="text-center">{t('부서')}</th>
        <th className="text-center">{t('증액 연월')}</th>
        <th className="text-center d-none">{t('계정')}</th>
        <th className="text-center">{t('계정명')}</th>
        <th className="text-center">{t('최초 예산')}</th>
        <th className="text-center">{t('기 증액예산')}</th>
        <th className="text-center">{t('실적')}</th>
        <th className="text-center">{t('예산 잔액')}</th>
        <th className="text-center">{t('증액 신청금액')}</th>
      </tr>
    </thead>
    <tbody>
      {account && (
        <tr>
          <td className="text-center">{department}</td>
          <td className="text-center">{yearMonth}</td>
          <td className="text-center d-none">{account.costElement ?? ''}</td>
          <td className="text-center" title={account.costElement ?? ''}>
            {account.accountName ?? ''}
          </td>
          <td className="text-right">
            {formatBudgetNumber(account.initialBudget)}
          </td>
          <td className="text-right">
            {formatBudgetNumber(account.increasedBudget)}
          </td>
          <td className="text-right">
            {formatBudgetNumber(account.actualTotal)}
          </td>
          <td className="text-right">
            {formatBudgetNumber(account.remaining)}
          </td>
          <td className="text-right">{amountCell}</td>
        </tr>
      )}
    </tbody>
  </>
);

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌 */
export const buildSapBudgetIncreaseContentHtml = (
  props: Omit<RowsProps, 'amountCell'> & { amount: string },
): string =>
  renderToStaticMarkup(
    <table className="table table-orders table-bordered table-striped table-responsive no-margin">
      <SapBudgetIncreaseRows
        {...props}
        amountCell={<span id="eas_budget_amt_box">{props.amount}</span>}
      />
    </table>,
  );

const DROP_TAGS = new Set(['style', 'script', 'link', 'input']);

/** 저장 content(레거시 문서 포함) 그대로 — 표만 공통 양식 표로 */
export const SapBudgetIncreaseSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (element.classList.contains('d-none')) return null;
      // 레거시 문서는 숨긴 계정 열 몫까지 col 9개 — 남기면 표 오른쪽에 빈 열이 생김
      if (tagName === 'col' && element.previousElementSibling) return null;
      if (tagName === 'table') {
        return (
          <ApprovalFormTable
            key={props.key as string}
            className={props.className as string | undefined}
          >
            {children}
          </ApprovalFormTable>
        );
      }
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <SapBudgetIncreaseRoot>{rendered}</SapBudgetIncreaseRoot>;
};
